import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDrop } from 'react-dnd';
import TableShape from './TableShape';
import VenueElementShape, { VENUE_ELEMENT_CONFIGS } from './VenueElementShape';
import CustomPieceShape, { CUSTOM_PIECE_CONFIGS } from './CustomPieceShape';
import TextBoxShape from './TextBoxShape';
import TextBoxFloatingPanel from './TextBoxFloatingPanel';
import QuickAssignPopover from './QuickAssignPopover';
import { SHAPE_CONFIGS } from './TableShape';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Slider } from '../ui/slider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { ZoomIn, ZoomOut, RotateCcw, Grid, Save, Trash2, Wand2, ImagePlus, X, Link2, Undo2, Redo2, FileImage, Type } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const CANVAS_WIDTH = 1200;
const CANVAS_HEIGHT = 1800;
const GRID_SIZE = 20;

// Draggable grouped table bounding box component - NO seats (seats are on pieces)
function GroupedTableBoundingBox({ 
  groupedTable, 
  minX, minY, maxX, maxY, 
  zoom, 
  isSelected, 
  onClick, 
  onMove,
  gridSize = 20,
  totalSeats = 0,
  occupiedSeats = 0,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const boxRef = useRef(null);
  
  const width = maxX - minX + 10;
  const height = maxY - minY + 10;
  
  const snapToGrid = useCallback((value) => {
    return Math.round(value / gridSize) * gridSize;
  }, [gridSize]);
  
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    
    setIsDragging(true);
    setDragStart({
      mouseX: e.clientX,
      mouseY: e.clientY,
      initialMinX: minX,
      initialMinY: minY,
    });
    
    // Don't select on mouseDown - let onClick handle selection
  }, [minX, minY]);
  
  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !dragStart) return;
    
    const deltaX = (e.clientX - dragStart.mouseX) / zoom;
    const deltaY = (e.clientY - dragStart.mouseY) / zoom;
    
    const snappedDeltaX = snapToGrid(deltaX);
    const snappedDeltaY = snapToGrid(deltaY);
    
    if (snappedDeltaX !== 0 || snappedDeltaY !== 0) {
      if (onMove) {
        onMove(groupedTable.id, snappedDeltaX, snappedDeltaY);
      }
      setDragStart({
        ...dragStart,
        mouseX: dragStart.mouseX + snappedDeltaX * zoom,
        mouseY: dragStart.mouseY + snappedDeltaY * zoom,
      });
    }
  }, [isDragging, dragStart, zoom, snapToGrid, onMove, groupedTable.id]);
  
  const handleMouseUp = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsDragging(false);
    setDragStart(null);
  }, []);
  
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);
  
  const boxLeft = (minX - 5) * zoom;
  const boxTop = (minY - 5) * zoom;
  
  return (
    <div
      ref={boxRef}
      className={`absolute cursor-move ${isSelected ? 'border-2 border-dashed border-yellow-400' : 'border border-dashed border-slate-500/30'}`}
      style={{
        left: boxLeft,
        top: boxTop,
        width: width * zoom,
        height: height * zoom,
        borderRadius: 8,
        zIndex: isSelected ? 15 : 5,
        backgroundColor: isSelected ? 'rgba(234, 179, 8, 0.03)' : 'transparent',
      }}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onClick={(e) => {
        e.stopPropagation();
        // Ensure dragging is stopped on click
        setIsDragging(false);
        setDragStart(null);
        onClick(e);
      }}
      data-testid={`grouped-table-${groupedTable.id}`}
    >
      {/* Group/Table Label */}
      <div 
        className={`absolute -top-6 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded text-xs whitespace-nowrap pointer-events-none ${
          isSelected ? 'bg-yellow-500 text-black font-semibold' : 'bg-slate-700 text-white'
        }`}
        style={{ fontSize: 10 * zoom }}
      >
        {groupedTable.table_name || 'Grouped Table'}
        {` (${occupiedSeats}/${totalSeats} seats)`}
      </div>
      
      {isSelected && (
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full z-20" />
      )}
    </div>
  );
}

export default function FloorPlanCanvas({ 
  tables, 
  guests = [], // guests for showing on hover
  venueElements = [], // venue elements (stage, bar, etc.)
  customPieces = [], // custom pieces for modular table building
  textBoxes = [], // text boxes for floor plan labels
  onUpdateTable, 
  onDeleteTable,
  onDuplicateTable,
  onUpdateVenueElement,
  onDeleteVenueElement,
  onDuplicateVenueElement,
  onAddCustomPiece,
  onUpdateCustomPiece,
  onDeleteCustomPiece,
  onDuplicateCustomPiece,
  onGroupPieces, // callback to group selected pieces into a table
  onAddTextBox, // callback to add a text box
  onUpdateTextBox, // callback to update a text box
  onDeleteTextBox, // callback to delete a text box
  onSaveLayout,
  onAutoLayout,
  saving,
  autoLayoutLoading = false,
  backgroundUrl = null,
  backgroundOpacity = 50,
  onUploadBackground,
  onRemoveBackground,
  onOpacityChange,
  uploadingBackground = false,
  // Final floor plan props
  finalFloorPlanUrl = null,
  onUploadFinalFloorPlan,
  onRemoveFinalFloorPlan,
  uploadingFinalFloorPlan = false,
  selectedTableId: externalSelectedId = null,
  selectedElementId: externalSelectedElementId = null,
  selectedPieceId: externalSelectedPieceId = null,
  selectedTextBoxId: externalSelectedTextBoxId = null,
  onTableSelect,
  onElementSelect,
  onPieceSelect,
  onTextBoxSelect,
  onAssignGuest, // callback for Quick Assign: (guestId, tableId) => void
  onRemoveGuestFromSeat, // callback to remove guest from seat: (guestId) => void
  onSwapGuests, // callback to swap two guests: (guest1Id, guest2Id) => void
  onMoveGroupedTable, // callback to move grouped table and all its pieces: (tableId, deltaX, deltaY) => void
  onUndo, // callback for undo action
  onRedo, // callback for redo action
  canUndo = false, // whether undo is available
  canRedo = false, // whether redo is available
}) {
  const { adminDarkMode } = useTheme();
  const [zoom, setZoom] = useState(1);
  const [showGrid, setShowGrid] = useState(true);
  const [internalSelectedId, setInternalSelectedId] = useState(null);
  const [internalSelectedElementId, setInternalSelectedElementId] = useState(null);
  const [internalSelectedPieceId, setInternalSelectedPieceId] = useState(null);
  const [internalSelectedTextBoxId, setInternalSelectedTextBoxId] = useState(null);
  const [localOpacity, setLocalOpacity] = useState(backgroundOpacity);
  const [quickAssign, setQuickAssign] = useState({ isOpen: false, position: { x: 0, y: 0 }, tableInfo: null });
  const [alignmentGuides, setAlignmentGuides] = useState({ vertical: [], horizontal: [] }); // For smart alignment visualization
  
  // Bulk selection state
  const [selectedTableIds, setSelectedTableIds] = useState([]);
  const [selectedElementIds, setSelectedElementIds] = useState([]);
  const [selectedPieceIds, setSelectedPieceIds] = useState([]); // For custom pieces
  
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const finalFloorPlanInputRef = useRef(null);
  const lastMousePosRef = useRef({ x: 0, y: 0 }); // Track mouse position for accurate drops
  
  // Use external selection if provided, otherwise use internal
  const selectedTableId = externalSelectedId !== null ? externalSelectedId : internalSelectedId;
  const selectedElementId = externalSelectedElementId !== null ? externalSelectedElementId : internalSelectedElementId;
  const selectedPieceId = externalSelectedPieceId !== null ? externalSelectedPieceId : internalSelectedPieceId;
  const selectedTextBoxId = externalSelectedTextBoxId !== null ? externalSelectedTextBoxId : internalSelectedTextBoxId;
  
  // Check if we're in bulk selection mode
  const isBulkSelecting = selectedTableIds.length > 0 || selectedElementIds.length > 0 || selectedPieceIds.length > 0;
  const totalSelected = selectedTableIds.length + selectedElementIds.length + selectedPieceIds.length;
  
  // Check if only pieces are selected (for Group button)
  const onlyPiecesSelected = selectedPieceIds.length >= 2 && selectedTableIds.length === 0 && selectedElementIds.length === 0;

  // Get all valid table IDs (existing tables)
  const validTableIds = new Set(tables.map(t => t.id));
  
  // Get unassigned guests (no table_id OR table_id points to a deleted/non-existent table)
  const unassignedGuests = guests.filter(g => !g.table_id || !validTableIds.has(g.table_id));
  
  // Debug logging for orphaned guest issue
  console.log('[FloorPlan Debug] Total guests:', guests.length);
  console.log('[FloorPlan Debug] Valid table IDs:', Array.from(validTableIds));
  console.log('[FloorPlan Debug] Unassigned guests:', unassignedGuests.length);
  if (guests.length > 0 && unassignedGuests.length === 0) {
    console.log('[FloorPlan Debug] All guests have valid table_id. Sample:', guests.slice(0, 3).map(g => ({ name: g.full_name, table_id: g.table_id })));
  }
  const orphanedGuests = guests.filter(g => g.table_id && !validTableIds.has(g.table_id));
  if (orphanedGuests.length > 0) {
    console.log('[FloorPlan Debug] ORPHANED guests found:', orphanedGuests.map(g => ({ name: g.full_name, table_id: g.table_id })));
  }

  // Sync local opacity with prop
  useEffect(() => {
    setLocalOpacity(backgroundOpacity);
  }, [backgroundOpacity]);
  
  // Track mouse position on the canvas for accurate drop calculations
  // This bypasses react-dnd's coordinate system which can be confused by CSS transforms
  useEffect(() => {
    const handleMouseMove = (e) => {
      lastMousePosRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);
  
  // Handle seat click for Quick Assign (both empty and occupied seats)
  const handleEmptySeatClick = useCallback((seatInfo) => {
    setQuickAssign({
      isOpen: true,
      position: { x: seatInfo.posX, y: seatInfo.posY },
      tableInfo: {
        tableId: seatInfo.tableId,
        tableName: seatInfo.tableName,
        seatIndex: seatInfo.seatIndex,
        currentGuest: seatInfo.currentGuest,
        isOccupied: seatInfo.isOccupied,
      },
    });
  }, []);
  
  // Handle guest assignment from Quick Assign
  const handleQuickAssign = useCallback((guestId, tableId, seatIndex) => {
    if (onAssignGuest) {
      onAssignGuest(guestId, tableId, seatIndex);
    }
  }, [onAssignGuest]);
  
  // Close Quick Assign popover
  const closeQuickAssign = useCallback(() => {
    setQuickAssign(prev => ({ ...prev, isOpen: false }));
  }, []);

  // Compute assigned guests (for swap feature) - only include guests assigned to existing tables
  const assignedGuests = guests.filter(g => g.table_id && validTableIds.has(g.table_id));

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && onUploadBackground) {
      onUploadBackground(file);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle final floor plan file select
  const handleFinalFloorPlanSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && onUploadFinalFloorPlan) {
      onUploadFinalFloorPlan(file);
    }
    // Reset input so same file can be selected again
    if (finalFloorPlanInputRef.current) {
      finalFloorPlanInputRef.current.value = '';
    }
  };

  // Handle opacity change with debounce
  const handleOpacityChange = (value) => {
    const opacity = value[0];
    setLocalOpacity(opacity);
  };

  const handleOpacityCommit = () => {
    if (onOpacityChange && localOpacity !== backgroundOpacity) {
      onOpacityChange(localOpacity);
    }
  };

  // Simple drop handler with grid snapping
  const [{ isOver }, drop] = useDrop({
    accept: ['TABLE', 'TABLE_SHAPE', 'VENUE_ELEMENT', 'VENUE_ELEMENT_NEW', 'CUSTOM_PIECE', 'CUSTOM_PIECE_NEW'],
    drop: (item, monitor) => {
      const canvasRect = canvasRef.current?.getBoundingClientRect();
      
      if (canvasRect) {
        // Helper to snap to grid
        const snapToGrid = (value) => Math.round(value / GRID_SIZE) * GRID_SIZE;
        
        // Smart snap to other pieces - finds nearest edge alignment
        const SNAP_THRESHOLD = 20; // pixels to trigger snap
        const ARC_SNAP_THRESHOLD = 60; // Larger threshold for arc-to-arc serpentine snapping
        
        const smartSnap = (x, y, itemWidth, itemHeight, itemId, itemPieceType) => {
          let snappedX = x;
          let snappedY = y;
          let didSnap = false;
          let bestSnapDistance = Infinity;
          
          // Get the config for this piece type
          const itemConfig = CUSTOM_PIECE_CONFIGS[itemPieceType];
          const isArc = itemConfig?.isSerpentine;
          
          // Get edges of the item being moved (bounding box)
          const itemLeft = x;
          const itemRight = x + itemWidth;
          const itemTop = y;
          const itemBottom = y + itemHeight;
          const itemCenterX = x + itemWidth / 2;
          const itemCenterY = y + itemHeight / 2;
          
          // Check against all other custom pieces
          customPieces.forEach(piece => {
            if (piece.id === itemId) return; // Skip self
            
            const pieceConfig = CUSTOM_PIECE_CONFIGS[piece.piece_type];
            const pieceWidth = piece.width || pieceConfig?.width || 100;
            const pieceHeight = piece.height || pieceConfig?.height || 60;
            const isPieceArc = pieceConfig?.isSerpentine;
            
            // SPECIAL CASE: Arc-to-Arc serpentine connection (priority over bounding box snapping)
            if (isArc && isPieceArc && pieceConfig?.connectionPoints && itemConfig?.connectionPoints) {
              // Get connection points relative to piece dimensions
              const itemConnRel = itemConfig.connectionPoints(itemWidth, itemHeight);
              const pieceConnRel = pieceConfig.connectionPoints(pieceWidth, pieceHeight);
              
              // Calculate absolute positions of target piece's connection points
              const pieceLeftConnAbs = { 
                x: piece.position_x + pieceConnRel.left.x, 
                y: piece.position_y + pieceConnRel.left.y 
              };
              const pieceRightConnAbs = { 
                x: piece.position_x + pieceConnRel.right.x, 
                y: piece.position_y + pieceConnRel.right.y 
              };
              
              // Calculate absolute positions of dragged item's connection points (current position)
              const itemLeftConnAbs = { x: x + itemConnRel.left.x, y: y + itemConnRel.left.y };
              const itemRightConnAbs = { x: x + itemConnRel.right.x, y: y + itemConnRel.right.y };
              
              // Case 1: Snap dragged item's RIGHT connection to target's LEFT connection
              // This chains: [dragged item] --> [target piece]
              const distRightToLeft = Math.sqrt(
                Math.pow(itemRightConnAbs.x - pieceLeftConnAbs.x, 2) + 
                Math.pow(itemRightConnAbs.y - pieceLeftConnAbs.y, 2)
              );
              
              if (distRightToLeft < ARC_SNAP_THRESHOLD && distRightToLeft < bestSnapDistance) {
                // To align: move the item so itemRightConn lands exactly on pieceLeftConn
                // New position = target connection point - item's relative connection offset
                snappedX = pieceLeftConnAbs.x - itemConnRel.right.x;
                snappedY = pieceLeftConnAbs.y - itemConnRel.right.y;
                bestSnapDistance = distRightToLeft;
                didSnap = true;
              }
              
              // Case 2: Snap dragged item's LEFT connection to target's RIGHT connection
              // This chains: [target piece] --> [dragged item]
              const distLeftToRight = Math.sqrt(
                Math.pow(itemLeftConnAbs.x - pieceRightConnAbs.x, 2) + 
                Math.pow(itemLeftConnAbs.y - pieceRightConnAbs.y, 2)
              );
              
              if (distLeftToRight < ARC_SNAP_THRESHOLD && distLeftToRight < bestSnapDistance) {
                // To align: move the item so itemLeftConn lands exactly on pieceRightConn
                snappedX = pieceRightConnAbs.x - itemConnRel.left.x;
                snappedY = pieceRightConnAbs.y - itemConnRel.left.y;
                bestSnapDistance = distLeftToRight;
                didSnap = true;
              }
              
              // If we found an arc-to-arc snap, skip bounding box snapping for this piece
              if (didSnap) return;
            }
            
            // Standard edge/bounding box snapping for non-arc or mixed piece types
            const pieceLeft = piece.position_x;
            const pieceRight = piece.position_x + pieceWidth;
            const pieceTop = piece.position_y;
            const pieceBottom = piece.position_y + pieceHeight;
            const pieceCenterX = piece.position_x + pieceWidth / 2;
            const pieceCenterY = piece.position_y + pieceHeight / 2;
            
            // Skip bounding box snapping for arc pieces - only use connection point snapping
            if (isArc && isPieceArc) return;
            
            // Snap left edge to left edge
            if (Math.abs(itemLeft - pieceLeft) < SNAP_THRESHOLD) {
              snappedX = pieceLeft;
            }
            // Snap left edge to right edge (side by side)
            if (Math.abs(itemLeft - pieceRight) < SNAP_THRESHOLD) {
              snappedX = pieceRight;
            }
            // Snap right edge to left edge
            if (Math.abs(itemRight - pieceLeft) < SNAP_THRESHOLD) {
              snappedX = pieceLeft - itemWidth;
            }
            // Snap right edge to right edge
            if (Math.abs(itemRight - pieceRight) < SNAP_THRESHOLD) {
              snappedX = pieceRight - itemWidth;
            }
            // Snap center X to center X
            if (Math.abs(itemCenterX - pieceCenterX) < SNAP_THRESHOLD) {
              snappedX = pieceCenterX - itemWidth / 2;
            }
            
            // Snap top edge to top edge
            if (Math.abs(itemTop - pieceTop) < SNAP_THRESHOLD) {
              snappedY = pieceTop;
            }
            // Snap top edge to bottom edge (stacking)
            if (Math.abs(itemTop - pieceBottom) < SNAP_THRESHOLD) {
              snappedY = pieceBottom;
            }
            // Snap bottom edge to top edge
            if (Math.abs(itemBottom - pieceTop) < SNAP_THRESHOLD) {
              snappedY = pieceTop - itemHeight;
            }
            // Snap bottom edge to bottom edge
            if (Math.abs(itemBottom - pieceBottom) < SNAP_THRESHOLD) {
              snappedY = pieceBottom - itemHeight;
            }
            // Snap center Y to center Y
            if (Math.abs(itemCenterY - pieceCenterY) < SNAP_THRESHOLD) {
              snappedY = pieceCenterY - itemHeight / 2;
            }
          });
          
          // If no smart snap, use grid snap
          if (snappedX === x) snappedX = snapToGrid(x);
          if (snappedY === y) snappedY = snapToGrid(y);
          
          return { x: snappedX, y: snappedY };
        };
        
        // Handle custom piece drops (existing piece being moved)
        if (item.type === 'CUSTOM_PIECE') {
          // Check if this is an Arc piece
          const pieceConfig = CUSTOM_PIECE_CONFIGS[item.piece_type];
          const isArc = pieceConfig?.isSerpentine;
          
          if (isArc) {
            // ARC: Use cursor position and center piece on cursor
            // This matches exactly what the drag preview shows
            const cursor = monitor.getClientOffset();
            if (cursor && onUpdateCustomPiece) {
              const x = (cursor.x - canvasRect.left) / zoom - item.width / 2;
              const y = (cursor.y - canvasRect.top) / zoom - item.height / 2;
              const finalX = Math.round(Math.max(0, Math.min(x, CANVAS_WIDTH - item.width)));
              const finalY = Math.round(Math.max(0, Math.min(y, CANVAS_HEIGHT - item.height)));
              onUpdateCustomPiece(item.id, { position_x: finalX, position_y: finalY });
            }
          } else {
            // Other pieces: use delta-based calculation with snapping
            const diff = monitor.getDifferenceFromInitialOffset();
            if (diff && onUpdateCustomPiece) {
              const originalPiece = customPieces.find(p => p.id === item.id);
              if (originalPiece) {
                const newX = (originalPiece.position_x || 0) + diff.x / zoom;
                const newY = (originalPiece.position_y || 0) + diff.y / zoom;
                const snapped = smartSnap(newX, newY, item.width, item.height, item.id, item.piece_type);
                const clampedX = Math.round(Math.max(0, Math.min(snapped.x, CANVAS_WIDTH - item.width)));
                const clampedY = Math.round(Math.max(0, Math.min(snapped.y, CANVAS_HEIGHT - item.height)));
                onUpdateCustomPiece(item.id, { position_x: clampedX, position_y: clampedY });
              }
            }
          }
          return undefined;
        }
        
        // Handle new custom piece from library
        if (item.type === 'CUSTOM_PIECE_NEW') {
          const offset = monitor.getClientOffset();
          if (offset) {
            // Calculate position - center piece on cursor
            const x = (offset.x - canvasRect.left) / zoom - item.width / 2;
            const y = (offset.y - canvasRect.top) / zoom - item.height / 2;
            
            // Check if this is an Arc piece
            const pieceConfig = CUSTOM_PIECE_CONFIGS[item.piece_type];
            const isArc = pieceConfig?.isSerpentine;
            
            let finalX, finalY;
            if (isArc) {
              finalX = x;
              finalY = y;
            } else {
              const snapped = smartSnap(x, y, item.width, item.height, null, item.piece_type);
              finalX = snapped.x;
              finalY = snapped.y;
            }
            
            const clampedX = Math.round(Math.max(0, Math.min(finalX, CANVAS_WIDTH - item.width)));
            const clampedY = Math.round(Math.max(0, Math.min(finalY, CANVAS_HEIGHT - item.height)));
            return { x: clampedX, y: clampedY, isNew: true };
          }
          return undefined;
        }
        
        // Handle venue element drops
        if (item.type === 'VENUE_ELEMENT') {
          // Use getSourceClientOffset for exact visual positioning
          const sourceOffset = monitor.getSourceClientOffset();
          if (sourceOffset && onUpdateVenueElement) {
            const newX = (sourceOffset.x - canvasRect.left) / zoom;
            const newY = (sourceOffset.y - canvasRect.top) / zoom;
            
            const snappedX = snapToGrid(newX);
            const snappedY = snapToGrid(newY);
            
            const clampedX = Math.round(Math.max(0, Math.min(snappedX, CANVAS_WIDTH - item.width)));
            const clampedY = Math.round(Math.max(0, Math.min(snappedY, CANVAS_HEIGHT - item.height)));
            
            onUpdateVenueElement(item.id, { position_x: clampedX, position_y: clampedY });
          }
          return undefined;
        }
        
        // Handle new venue element from library
        if (item.type === 'VENUE_ELEMENT_NEW') {
          const offset = monitor.getClientOffset();
          if (offset) {
            const x = (offset.x - canvasRect.left) / zoom - item.width / 2;
            const y = (offset.y - canvasRect.top) / zoom - item.height / 2;
            
            const snappedX = snapToGrid(x);
            const snappedY = snapToGrid(y);
            
            const clampedX = Math.round(Math.max(0, Math.min(snappedX, CANVAS_WIDTH - item.width)));
            const clampedY = Math.round(Math.max(0, Math.min(snappedY, CANVAS_HEIGHT - item.height)));
            return { x: clampedX, y: clampedY, isNew: true };
          }
          return undefined;
        }
        
        // Handle table drops
        const shape = item.shape || 'round';
        const config = SHAPE_CONFIGS[shape] || SHAPE_CONFIGS.round;
        const baseWidth = item.width || config.width;
        const baseHeight = item.height || config.height;
        
        // For existing tables: Use react-dnd's getClientOffset as PRIMARY source
        // Fall back to lastMousePosRef for edge cases
        if (item.id) {
          // PRIMARY: Use react-dnd's getClientOffset() - most reliable
          const offset = monitor.getClientOffset();
          
          // FALLBACK: Use our tracked mouse position (for cases where getClientOffset fails)
          const mouseX = lastMousePosRef.current.x;
          const mouseY = lastMousePosRef.current.y;
          
          // Prefer react-dnd's offset, fall back to tracked mouse position
          const hasValidMouseRef = mouseX !== 0 || mouseY !== 0;
          const finalMouseX = offset?.x ?? (hasValidMouseRef ? mouseX : null);
          const finalMouseY = offset?.y ?? (hasValidMouseRef ? mouseY : null);
          
          if (finalMouseX !== null && finalMouseY !== null && canvasRef.current) {
            // Get fresh canvas rect at drop time
            const rect = canvasRef.current.getBoundingClientRect();
            
            // Calculate position relative to canvas, accounting for zoom
            const relX = (finalMouseX - rect.left) / zoom;
            const relY = (finalMouseY - rect.top) / zoom;
            
            // The drag preview is centered on cursor, so cursor = center of table
            // We just need to calculate logical top-left from center
            let newX = relX - baseWidth / 2;
            let newY = relY - baseHeight / 2;
            
            // Snap to grid
            const snappedX = snapToGrid(newX);
            const snappedY = snapToGrid(newY);
            
            // Clamp to canvas bounds (allow 0 as minimum)
            const clampedX = Math.round(Math.max(0, Math.min(snappedX, CANVAS_WIDTH - 20)));
            const clampedY = Math.round(Math.max(0, Math.min(snappedY, CANVAS_HEIGHT - 20)));
            
            onUpdateTable(item.id, { position_x: clampedX, position_y: clampedY });
          }
        } else if (item.type === 'TABLE_SHAPE') {
          // For new tables from library, use client offset and center on cursor
          const offset = monitor.getClientOffset();
          if (offset) {
            const x = (offset.x - canvasRect.left) / zoom - baseWidth / 2;
            const y = (offset.y - canvasRect.top) / zoom - baseHeight / 2;
            
            // Snap to grid
            const snappedX = snapToGrid(x);
            const snappedY = snapToGrid(y);
            
            // New tables start with no rotation, so use normal bounds
            const clampedX = Math.round(Math.max(0, Math.min(snappedX, CANVAS_WIDTH - baseWidth)));
            const clampedY = Math.round(Math.max(0, Math.min(snappedY, CANVAS_HEIGHT - baseHeight)));
            return { x: clampedX, y: clampedY, isNew: true };
          }
        }
      }
      return undefined;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  // Handle table selection (supports Shift+Click for bulk selection)
  const handleTableClick = useCallback((tableId, event) => {
    // Check if Shift key is held for bulk selection
    const isShiftKey = event?.shiftKey || false;
    
    if (isShiftKey) {
      // Bulk selection mode
      setSelectedTableIds(prev => {
        if (prev.includes(tableId)) {
          // Remove from selection
          return prev.filter(id => id !== tableId);
        } else {
          // Add to selection
          return [...prev, tableId];
        }
      });
      // Clear single selection when bulk selecting
      setInternalSelectedId(null);
      if (onTableSelect) {
        onTableSelect(null);
      }
    } else {
      // Normal single selection - clear bulk selection first
      setSelectedTableIds([]);
      setSelectedElementIds([]);
      setSelectedPieceIds([]); // Also clear piece selection
      
      const newSelectedId = tableId === selectedTableId ? null : tableId;
      setInternalSelectedId(newSelectedId);
      setInternalSelectedElementId(null); // Deselect venue element
      setInternalSelectedPieceId(null); // Deselect piece
      // Notify parent of selection change
      if (onTableSelect) {
        onTableSelect(newSelectedId);
      }
      if (onElementSelect) {
        onElementSelect(null);
      }
      if (onPieceSelect) {
        onPieceSelect(null);
      }
    }
  }, [selectedTableId, onTableSelect, onElementSelect, onPieceSelect]);

  // Handle venue element selection (supports Shift+Click for bulk selection)
  const handleElementClick = useCallback((elementId, event) => {
    // Check if Shift key is held for bulk selection
    const isShiftKey = event?.shiftKey || false;
    
    if (isShiftKey) {
      // Bulk selection mode
      setSelectedElementIds(prev => {
        if (prev.includes(elementId)) {
          // Remove from selection
          return prev.filter(id => id !== elementId);
        } else {
          // Add to selection
          return [...prev, elementId];
        }
      });
      // Clear single selection when bulk selecting
      setInternalSelectedElementId(null);
      if (onElementSelect) {
        onElementSelect(null);
      }
    } else {
      // Normal single selection - clear bulk selection first
      setSelectedTableIds([]);
      setSelectedElementIds([]);
      
      const newSelectedId = elementId === selectedElementId ? null : elementId;
      setInternalSelectedElementId(newSelectedId);
      setInternalSelectedId(null); // Deselect table
      // Notify parent of selection change
      if (onElementSelect) {
        onElementSelect(newSelectedId);
      }
      if (onTableSelect) {
        onTableSelect(null);
      }
    }
  }, [selectedElementId, onElementSelect, onTableSelect]);

  // Handle custom piece selection (supports Shift+Click for bulk selection)
  const handlePieceClick = useCallback((pieceId, event) => {
    const isShiftKey = event?.shiftKey || false;
    
    if (isShiftKey) {
      // Bulk selection mode - can select multiple pieces to group
      setSelectedPieceIds(prev => {
        if (prev.includes(pieceId)) {
          return prev.filter(id => id !== pieceId);
        } else {
          return [...prev, pieceId];
        }
      });
      // Clear single selection
      setInternalSelectedId(null);
      setInternalSelectedElementId(null);
      setInternalSelectedPieceId(null);
      setInternalSelectedTextBoxId(null);
      if (onTableSelect) onTableSelect(null);
      if (onElementSelect) onElementSelect(null);
      if (onPieceSelect) onPieceSelect(null);
      if (onTextBoxSelect) onTextBoxSelect(null);
    } else {
      // Normal single selection - clear all bulk selections and select this piece
      setSelectedTableIds([]);
      setSelectedElementIds([]);
      setSelectedPieceIds([]);
      setInternalSelectedId(null);
      setInternalSelectedElementId(null);
      setInternalSelectedPieceId(pieceId);
      setInternalSelectedTextBoxId(null);
      if (onTableSelect) onTableSelect(null);
      if (onElementSelect) onElementSelect(null);
      if (onPieceSelect) onPieceSelect(pieceId);
      if (onTextBoxSelect) onTextBoxSelect(null);
    }
  }, [onTableSelect, onElementSelect, onPieceSelect, onTextBoxSelect]);

  // Handle text box selection
  const handleTextBoxClick = useCallback((textBoxId) => {
    setSelectedTableIds([]);
    setSelectedElementIds([]);
    setSelectedPieceIds([]);
    setInternalSelectedId(null);
    setInternalSelectedElementId(null);
    setInternalSelectedPieceId(null);
    setInternalSelectedTextBoxId(textBoxId);
    if (onTableSelect) onTableSelect(null);
    if (onElementSelect) onElementSelect(null);
    if (onPieceSelect) onPieceSelect(null);
    if (onTextBoxSelect) onTextBoxSelect(textBoxId);
  }, [onTableSelect, onElementSelect, onPieceSelect, onTextBoxSelect]);

  // Handle adding a new text box
  const handleAddTextBox = useCallback(() => {
    if (onAddTextBox) {
      // Find a good position near existing content
      // Use the center of placed tables if any, otherwise use canvas center
      const placedTables = tables.filter(t => t.position_x != null && t.position_y != null);
      
      let centerX, centerY;
      if (placedTables.length > 0) {
        // Calculate centroid of placed tables
        const sumX = placedTables.reduce((sum, t) => sum + t.position_x, 0);
        const sumY = placedTables.reduce((sum, t) => sum + t.position_y, 0);
        centerX = Math.round((sumX / placedTables.length) / GRID_SIZE) * GRID_SIZE;
        centerY = Math.round((sumY / placedTables.length - 100) / GRID_SIZE) * GRID_SIZE; // Slightly above center
      } else {
        centerX = Math.round((CANVAS_WIDTH / 2) / GRID_SIZE) * GRID_SIZE;
        centerY = Math.round((CANVAS_HEIGHT / 4) / GRID_SIZE) * GRID_SIZE;
      }
      
      // Ensure within bounds
      centerX = Math.max(100, Math.min(centerX, CANVAS_WIDTH - 200));
      centerY = Math.max(50, Math.min(centerY, CANVAS_HEIGHT - 100));
      
      onAddTextBox({
        text: 'Text',
        position_x: centerX,
        position_y: centerY,
        width: 150,
        height: 40,
        rotation: 0,
        font_size: 16,
        font_color: '#000000',
        background_color: null,
        text_align: 'center',
      });
    }
  }, [onAddTextBox, tables]);

  // Handle keyboard shortcuts - supports bulk delete
  const handleKeyboardDelete = useCallback(() => {
    // Check for bulk selection first
    if (selectedTableIds.length > 0 || selectedElementIds.length > 0 || selectedPieceIds.length > 0) {
      // Delete all selected tables
      selectedTableIds.forEach(tableId => {
        onDeleteTable(tableId);
      });
      // Delete all selected elements
      selectedElementIds.forEach(elementId => {
        if (onDeleteVenueElement) {
          onDeleteVenueElement(elementId);
        }
      });
      // Delete all selected pieces
      selectedPieceIds.forEach(pieceId => {
        if (onDeleteCustomPiece) {
          onDeleteCustomPiece(pieceId);
        }
      });
      // Clear bulk selection
      setSelectedTableIds([]);
      setSelectedElementIds([]);
      setSelectedPieceIds([]);
      return;
    }
    
    // Single item delete
    if (selectedTableId) {
      onDeleteTable(selectedTableId);
      setInternalSelectedId(null);
      if (onTableSelect) {
        onTableSelect(null);
      }
    }
    if (selectedElementId && onDeleteVenueElement) {
      onDeleteVenueElement(selectedElementId);
      setInternalSelectedElementId(null);
      if (onElementSelect) {
        onElementSelect(null);
      }
    }
  }, [selectedTableId, selectedElementId, selectedTableIds, selectedElementIds, selectedPieceIds, onDeleteTable, onDeleteVenueElement, onDeleteCustomPiece, onTableSelect, onElementSelect]);

  // Tables that have been placed on the canvas (defined early for use in useEffect)
  const placedTables = tables.filter(t => t.position_x !== null && t.position_y !== null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle keyboard shortcuts when typing in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        return;
      }
      
      // Delete or Backspace key to remove selected item(s)
      const hasSelection = selectedTableId || selectedElementId || selectedTableIds.length > 0 || selectedElementIds.length > 0 || selectedPieceIds.length > 0;
      if (hasSelection && (e.key === 'Delete' || e.key === 'Backspace')) {
        // Prevent backspace from navigating back in browser
        e.preventDefault();
        handleKeyboardDelete();
      }
      
      // Escape clears all selection
      if (e.key === 'Escape') {
        setInternalSelectedId(null);
        setInternalSelectedElementId(null);
        setSelectedTableIds([]);
        setSelectedElementIds([]);
        setSelectedPieceIds([]);
        if (onTableSelect) {
          onTableSelect(null);
        }
        if (onElementSelect) {
          onElementSelect(null);
        }
        if (onPieceSelect) {
          onPieceSelect(null);
        }
      }
      
      // Arrow keys to move selected element
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        const moveAmount = e.shiftKey ? GRID_SIZE : 5; // Shift = grid snap, normal = 5px
        let deltaX = 0;
        let deltaY = 0;
        
        switch (e.key) {
          case 'ArrowUp': deltaY = -moveAmount; break;
          case 'ArrowDown': deltaY = moveAmount; break;
          case 'ArrowLeft': deltaX = -moveAmount; break;
          case 'ArrowRight': deltaX = moveAmount; break;
        }
        
        // Move selected table
        if (selectedTableId && onUpdateTable) {
          e.preventDefault();
          const table = tables.find(t => t.id === selectedTableId);
          if (table && table.position_x !== null) {
            onUpdateTable(selectedTableId, {
              position_x: (table.position_x || 0) + deltaX,
              position_y: (table.position_y || 0) + deltaY,
            });
          }
        }
        
        // Move selected venue element
        if (selectedElementId && onUpdateVenueElement) {
          e.preventDefault();
          const element = venueElements.find(el => el.id === selectedElementId);
          if (element) {
            onUpdateVenueElement(selectedElementId, {
              position_x: Math.max(0, (element.position_x || 0) + deltaX),
              position_y: Math.max(0, (element.position_y || 0) + deltaY),
            });
          }
        }
        
        // Move selected custom piece
        if (selectedPieceId && onUpdateCustomPiece) {
          e.preventDefault();
          const piece = customPieces.find(p => p.id === selectedPieceId);
          if (piece) {
            onUpdateCustomPiece(selectedPieceId, {
              position_x: Math.max(0, (piece.position_x || 0) + deltaX),
              position_y: Math.max(0, (piece.position_y || 0) + deltaY),
            });
          }
        }
      }
      
      // Ctrl/Cmd + A to select all
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        const allTableIds = placedTables.map(t => t.id);
        const allElementIds = venueElements.map(el => el.id);
        const allPieceIds = customPieces.map(p => p.id);
        setSelectedTableIds(allTableIds);
        setSelectedElementIds(allElementIds);
        setSelectedPieceIds(allPieceIds);
        // Clear single selection
        setInternalSelectedId(null);
        setInternalSelectedElementId(null);
        if (onTableSelect) onTableSelect(null);
        if (onElementSelect) onElementSelect(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedTableId, selectedElementId, selectedPieceId, selectedTableIds, selectedElementIds, selectedPieceIds, handleKeyboardDelete, onTableSelect, onElementSelect, onPieceSelect, onUpdateTable, onUpdateVenueElement, onUpdateCustomPiece, tables, placedTables, venueElements, customPieces]);

  // Zoom controls
  const handleZoomIn = () => setZoom(z => Math.min(z + 0.1, 2));
  const handleZoomOut = () => setZoom(z => Math.max(z - 0.1, 0.5));
  const handleResetZoom = () => setZoom(1);
  
  return (
    <div className="flex flex-col gap-4">
      {/* Toolbar - 2 rows */}
      <div className={`p-3 rounded-lg border space-y-2 ${adminDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-slate-200 shadow-sm'}`}>
        {/* Row 1: Zoom, Grid, Upload buttons */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleZoomOut}
              className={adminDarkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}
              data-testid="zoom-out-btn"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className={`text-sm min-w-[50px] text-center ${adminDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              {Math.round(zoom * 100)}%
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleZoomIn}
              className={adminDarkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}
              data-testid="zoom-in-btn"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetZoom}
              className={adminDarkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}
              data-testid="reset-zoom-btn"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
            <div className={`h-6 w-px mx-1 ${adminDarkMode ? 'bg-slate-700' : 'bg-slate-300'}`} />
            <Button
              variant={showGrid ? "default" : "outline"}
              size="sm"
              onClick={() => setShowGrid(!showGrid)}
              className={showGrid 
                ? "admin-accent-bg" 
                : adminDarkMode ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 text-slate-600 hover:bg-slate-100"
              }
              data-testid="toggle-grid-btn"
            >
              <Grid className="h-4 w-4 mr-1" />
              Grid
            </Button>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Final Floor Plan Upload - HIDDEN: Temporarily disabled pending review */}
            {false && onUploadFinalFloorPlan && (
              <>
                <input
                  ref={finalFloorPlanInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={handleFinalFloorPlanSelect}
                />
                {!finalFloorPlanUrl ? (
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => finalFloorPlanInputRef.current?.click()}
                          disabled={uploadingFinalFloorPlan}
                          className="border-emerald-700 text-emerald-400 hover:bg-emerald-900/20"
                          data-testid="upload-final-floor-plan-btn"
                        >
                          <FileImage className="h-4 w-4 mr-1" />
                          {uploadingFinalFloorPlan ? 'Uploading...' : 'Final Plan'}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className={`p-3 ${adminDarkMode ? 'bg-slate-800 border-slate-700 text-slate-200' : 'bg-white border-slate-200 text-slate-700'}`}>
                        <div className="text-xs space-y-1">
                          <p className="font-medium text-emerald-400">For Guest Page</p>
                          <p><span className={adminDarkMode ? 'text-slate-400' : 'text-slate-500'}>Recommended:</span> 1200x900px</p>
                          <p><span className={adminDarkMode ? 'text-slate-400' : 'text-slate-500'}>Max:</span> 5MB | PNG, JPG</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-emerald-400">Final ✓</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onRemoveFinalFloorPlan}
                      className="border-red-900/50 text-red-400 hover:bg-red-900/20 h-7 px-2"
                      data-testid="remove-final-floor-plan-btn"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </>
            )}
            
            {/* Background Image Controls - HIDDEN: Temporarily disabled pending review */}
            {false && onUploadBackground && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                {!backgroundUrl ? (
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingBackground}
                          className={adminDarkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}
                          data-testid="upload-bg-btn"
                        >
                          <ImagePlus className="h-4 w-4 mr-1" />
                          {uploadingBackground ? 'Uploading...' : 'Venue Image'}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className={`p-3 ${adminDarkMode ? 'bg-slate-800 border-slate-700 text-slate-200' : 'bg-white border-slate-200 text-slate-700'}`}>
                        <div className="text-xs space-y-1">
                          <p><span className={adminDarkMode ? 'text-slate-400' : 'text-slate-500'}>Recommended:</span> 1200x900px</p>
                          <p><span className={adminDarkMode ? 'text-slate-400' : 'text-slate-500'}>Max:</span> 5MB | PNG, JPG</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className={`flex items-center gap-2 px-2 py-1 rounded ${adminDarkMode ? 'bg-slate-800/50' : 'bg-slate-100'}`}>
                      <span className={`text-xs ${adminDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Opacity:</span>
                      <Slider
                        value={[localOpacity]}
                        onValueChange={handleOpacityChange}
                        onValueCommit={handleOpacityCommit}
                        min={0}
                        max={100}
                        step={5}
                        className="w-16"
                      />
                      <span className={`text-xs w-7 ${adminDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{localOpacity}%</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onRemoveBackground}
                      className="border-red-900/50 text-red-400 hover:bg-red-900/20 h-7 px-2"
                      data-testid="remove-bg-btn"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </>
            )}
            
            {/* Auto-Arrange */}
            {onAutoLayout && tables.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={onAutoLayout}
                disabled={autoLayoutLoading}
                className={adminDarkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}
                data-testid="auto-layout-btn"
              >
                <Wand2 className="h-4 w-4 mr-1" />
                {autoLayoutLoading ? 'Arranging...' : 'Auto-Arrange'}
              </Button>
            )}
          </div>
        </div>
        
        {/* Row 2: Undo/Redo, Add Text, Selection controls, Save */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            {/* Undo/Redo buttons */}
            {(onUndo || onRedo) && (
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onUndo}
                  disabled={!canUndo}
                  className={`h-8 w-8 p-0 ${adminDarkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}
                  title="Undo (Ctrl+Z)"
                  data-testid="undo-btn"
                >
                  <Undo2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRedo}
                  disabled={!canRedo}
                  className={`h-8 w-8 p-0 ${adminDarkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}
                  title="Redo (Ctrl+Shift+Z)"
                  data-testid="redo-btn"
                >
                  <Redo2 className="h-4 w-4" />
                </Button>
              </div>
            )}
            
            {/* Add Text Box button */}
            {onAddTextBox && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddTextBox}
                className={adminDarkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}
                title="Add Text Box"
                data-testid="add-text-box-btn"
              >
                <Type className="h-4 w-4 mr-1" />
                Add Text
              </Button>
            )}
            
            {/* Bulk selection indicator */}
            {isBulkSelecting && (
              <div className="bg-[#D4AF37]/20 text-[#D4AF37] px-3 py-1 rounded text-sm font-medium">
                {totalSelected} selected
              </div>
            )}
            
            {/* Group button - shows when 2+ pieces are selected */}
            {onlyPiecesSelected && onGroupPieces && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const piecesToGroup = customPieces.filter(p => selectedPieceIds.includes(p.id));
                  onGroupPieces(piecesToGroup);
                  setSelectedPieceIds([]);
                }}
                className="border-blue-500/50 text-blue-400 hover:bg-blue-900/20"
                data-testid="group-pieces-btn"
              >
                <Link2 className="h-4 w-4 mr-1" />
                Group ({selectedPieceIds.length})
              </Button>
            )}
            
            {/* Delete button - shows for single or bulk selection */}
            {(selectedTableId || selectedElementId || isBulkSelecting) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // Handle bulk delete
                  if (isBulkSelecting) {
                    selectedTableIds.forEach(tableId => {
                      onDeleteTable(tableId);
                    });
                    selectedElementIds.forEach(elementId => {
                      if (onDeleteVenueElement) {
                        onDeleteVenueElement(elementId);
                      }
                    });
                    selectedPieceIds.forEach(pieceId => {
                      if (onDeleteCustomPiece) {
                        onDeleteCustomPiece(pieceId);
                      }
                    });
                    setSelectedTableIds([]);
                    setSelectedElementIds([]);
                    setSelectedPieceIds([]);
                    return;
                  }
                  
                  // Single item delete
                  if (selectedTableId) {
                    onDeleteTable(selectedTableId);
                    setInternalSelectedId(null);
                    if (onTableSelect) {
                      onTableSelect(null);
                    }
                  }
                  if (selectedElementId && onDeleteVenueElement) {
                    onDeleteVenueElement(selectedElementId);
                    setInternalSelectedElementId(null);
                    if (onElementSelect) {
                      onElementSelect(null);
                    }
                  }
                }}
                className="border-red-900/50 text-red-400 hover:bg-red-900/20"
                data-testid="delete-selected-btn"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {isBulkSelecting ? `Delete ${totalSelected}` : 'Remove'}
              </Button>
            )}
            
            {/* Clear Selection button (shows during bulk selection) */}
            {isBulkSelecting && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedTableIds([]);
                  setSelectedElementIds([]);
                  setSelectedPieceIds([]);
                }}
                className={adminDarkMode ? 'border-slate-600 text-slate-400 hover:bg-slate-700' : 'border-slate-300 text-slate-500 hover:bg-slate-100'}
                data-testid="clear-selection-btn"
              >
                Clear
              </Button>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Select All button */}
            {(placedTables.length > 0 || venueElements.length > 0 || customPieces.length > 0) && !isBulkSelecting && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedTableIds(placedTables.map(t => t.id));
                  setSelectedElementIds(venueElements.map(el => el.id));
                  setSelectedPieceIds(customPieces.map(p => p.id));
                  setInternalSelectedId(null);
                  setInternalSelectedElementId(null);
                  if (onTableSelect) onTableSelect(null);
                  if (onElementSelect) onElementSelect(null);
                }}
                className={adminDarkMode ? 'border-slate-600 text-slate-400 hover:bg-slate-700' : 'border-slate-300 text-slate-500 hover:bg-slate-100'}
                data-testid="select-all-btn"
              >
                Select All
              </Button>
            )}
            
            <Button
              size="sm"
              onClick={onSaveLayout}
              disabled={saving}
              className="admin-accent-bg"
              data-testid="save-layout-btn"
            >
              <Save className="h-4 w-4 mr-1" />
              {saving ? 'Saving...' : 'Save Layout'}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Canvas Area */}
      <div className={`overflow-auto rounded-lg border p-4 ${adminDarkMode ? 'bg-slate-900/30 border-slate-700' : 'bg-slate-100 border-slate-200'}`}>
        <div
          ref={(node) => {
            drop(node);
            canvasRef.current = node;
          }}
          className="relative mx-auto overflow-visible"
          style={{
            width: CANVAS_WIDTH * zoom,
            height: CANVAS_HEIGHT * zoom,
            backgroundColor: adminDarkMode ? '#0f172a' : '#f1f5f9',
            borderRadius: '8px',
          }}
          onClick={(e) => {
            // Don't deselect if shift is held (user might be trying to add to selection)
            if (e.shiftKey) return;
            
            // Only deselect if click is directly on the canvas background (not on any child element)
            if (e.target === e.currentTarget) {
              setInternalSelectedId(null);
              setInternalSelectedElementId(null);
              setInternalSelectedPieceId(null);
              setInternalSelectedTextBoxId(null);
              setSelectedTableIds([]);
              setSelectedElementIds([]);
              setSelectedPieceIds([]);
              if (onTableSelect) {
                onTableSelect(null);
              }
              if (onElementSelect) {
                onElementSelect(null);
              }
              if (onPieceSelect) {
                onPieceSelect(null);
              }
              if (onTextBoxSelect) {
                onTextBoxSelect(null);
              }
            }
          }}
          data-testid="floor-plan-canvas"
        >
          {/* Background Image Layer */}
          {backgroundUrl && (
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat pointer-events-none"
              style={{
                backgroundImage: `url(${backgroundUrl})`,
                opacity: localOpacity / 100,
              }}
              data-testid="floor-plan-bg-image"
            />
          )}
          
          {/* Grid Overlay */}
          {showGrid && (
            <div 
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: `
                  linear-gradient(to right, rgba(148, 163, 184, 0.15) 1px, transparent 1px),
                  linear-gradient(to bottom, rgba(148, 163, 184, 0.15) 1px, transparent 1px)
                `,
                backgroundSize: `${GRID_SIZE * zoom}px ${GRID_SIZE * zoom}px`,
              }}
            />
          )}
          
          {/* Alignment Guides - shown when dragging pieces near alignment */}
          {alignmentGuides.vertical.map((x, i) => (
            <div
              key={`v-${i}`}
              className="absolute pointer-events-none bg-cyan-400"
              style={{
                left: x * zoom,
                top: 0,
                width: 1,
                height: CANVAS_HEIGHT * zoom,
                opacity: 0.8,
                zIndex: 100,
              }}
            />
          ))}
          {alignmentGuides.horizontal.map((y, i) => (
            <div
              key={`h-${i}`}
              className="absolute pointer-events-none bg-cyan-400"
              style={{
                left: 0,
                top: y * zoom,
                width: CANVAS_WIDTH * zoom,
                height: 1,
                opacity: 0.8,
                zIndex: 100,
              }}
            />
          ))}
          
          {/* Placed Tables - filter out 'grouped' shape tables (they're just logical containers) */}
          {placedTables.filter(table => table.shape !== 'grouped').map(table => (
            <TableShape
              key={table.id}
              table={table}
              zoom={zoom}
              isSelected={selectedTableId === table.id || selectedTableIds.includes(table.id)}
              onClick={(e) => handleTableClick(table.id, e)}
              onPositionChange={(x, y) => onUpdateTable(table.id, { position_x: x, position_y: y })}
              onUpdateLabel={(tableId, labelData) => onUpdateTable(tableId, labelData)}
              onResize={(tableId, resizeData) => onUpdateTable(tableId, resizeData)}
              onRotate={(tableId, rotateData) => onUpdateTable(tableId, rotateData)}
              onDuplicate={onDuplicateTable}
              onDelete={onDeleteTable}
              gridSize={GRID_SIZE}
              guests={guests}
              showSeats={true}
              onEmptySeatClick={onAssignGuest ? handleEmptySeatClick : undefined}
            />
          ))}
          
          {/* Venue Elements */}
          {venueElements.map(element => (
            <VenueElementShape
              key={element.id}
              element={element}
              zoom={zoom}
              isSelected={selectedElementId === element.id || selectedElementIds.includes(element.id)}
              onClick={(e) => handleElementClick(element.id, e)}
              onPositionChange={(x, y) => onUpdateVenueElement && onUpdateVenueElement(element.id, { position_x: x, position_y: y })}
              onSizeChange={(id, data) => onUpdateVenueElement && onUpdateVenueElement(id, data)}
              onDuplicate={onDuplicateVenueElement}
              onDelete={onDeleteVenueElement}
              gridSize={GRID_SIZE}
            />
          ))}
          
          {/* Custom Pieces (for modular table building) */}
          {customPieces.map(piece => {
            // Find if this piece belongs to a grouped table
            const parentTable = placedTables.find(t => 
              t.shape === 'grouped' && 
              t.grouped_piece_ids?.includes(piece.id)
            );
            
            return (
              <CustomPieceShape
                key={piece.id}
                piece={piece}
                zoom={zoom}
                isSelected={selectedPieceId === piece.id || selectedPieceIds.includes(piece.id) || 
                  (parentTable && (selectedTableId === parentTable.id || selectedTableIds.includes(parentTable.id)))}
                onClick={(e) => {
                  // If piece is part of a grouped table, select the table instead
                  if (parentTable) {
                    handleTableClick(parentTable.id, e);
                  } else {
                    handlePieceClick(piece.id, e);
                  }
                }}
                onPositionChange={(id, x, y) => onUpdateCustomPiece && onUpdateCustomPiece(id, { position_x: x, position_y: y })}
                onSizeChange={(id, data) => onUpdateCustomPiece && onUpdateCustomPiece(id, data)}
                onSeatsChange={(id, newSeats) => onUpdateCustomPiece && onUpdateCustomPiece(id, { seats: newSeats })}
                onSeatOffsetsChange={(id, seatOffsets) => onUpdateCustomPiece && onUpdateCustomPiece(id, { seat_offsets: seatOffsets })}
                onDuplicate={onDuplicateCustomPiece}
                onDelete={onDeleteCustomPiece}
                gridSize={GRID_SIZE}
                showSeats={true}
                parentTable={parentTable}
                guests={guests}
                onSeatClick={onAssignGuest ? handleEmptySeatClick : undefined}
              />
            );
          })}
          
          {/* Grouped Table Labels & Bounding Boxes - Draggable */}
          {placedTables.filter(t => t.shape === 'grouped').map(groupedTable => {
            // Find all pieces belonging to this group
            const groupPieces = customPieces.filter(p => 
              groupedTable.grouped_piece_ids?.includes(p.id)
            );
            
            if (groupPieces.length === 0) return null;
            
            // Calculate bounding box
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            groupPieces.forEach(p => {
              minX = Math.min(minX, p.position_x);
              minY = Math.min(minY, p.position_y);
              maxX = Math.max(maxX, p.position_x + (p.width || 60));
              maxY = Math.max(maxY, p.position_y + (p.height || 60));
            });
            
            // Calculate total seats from all pieces in the group
            const totalSeats = groupPieces.reduce((sum, p) => sum + (p.seats || 4), 0);
            // Count guests assigned to this grouped table
            const tableGuests = guests.filter(g => g.table_id === groupedTable.id);
            const occupiedSeats = tableGuests.length;
            
            const isSelected = selectedTableId === groupedTable.id || selectedTableIds.includes(groupedTable.id);
            
            return (
              <GroupedTableBoundingBox
                key={`group-${groupedTable.id}`}
                groupedTable={groupedTable}
                minX={minX}
                minY={minY}
                maxX={maxX}
                maxY={maxY}
                zoom={zoom}
                isSelected={isSelected}
                onClick={(e) => handleTableClick(groupedTable.id, e)}
                onMove={onMoveGroupedTable}
                gridSize={GRID_SIZE}
                totalSeats={totalSeats}
                occupiedSeats={occupiedSeats}
              />
            );
          })}
          
          {/* Text Boxes */}
          {textBoxes.map(textBox => (
            <TextBoxShape
              key={textBox.id}
              textBox={textBox}
              zoom={zoom}
              isSelected={selectedTextBoxId === textBox.id}
              onClick={handleTextBoxClick}
              onUpdate={onUpdateTextBox}
              onDelete={onDeleteTextBox}
              gridSize={GRID_SIZE}
            />
          ))}
          
          {/* Floating Text Box Properties Panel */}
          {selectedTextBoxId && textBoxes.find(tb => tb.id === selectedTextBoxId) && (
            <TextBoxFloatingPanel
              textBox={textBoxes.find(tb => tb.id === selectedTextBoxId)}
              position={{
                x: (textBoxes.find(tb => tb.id === selectedTextBoxId)?.position_x || 0) * zoom,
                y: (textBoxes.find(tb => tb.id === selectedTextBoxId)?.position_y || 0) * zoom,
              }}
              zoom={zoom}
              onUpdate={onUpdateTextBox}
              onDelete={(id) => {
                onDeleteTextBox(id);
                setInternalSelectedTextBoxId(null);
                if (onTextBoxSelect) onTextBoxSelect(null);
              }}
              onClose={() => {
                setInternalSelectedTextBoxId(null);
                if (onTextBoxSelect) onTextBoxSelect(null);
              }}
            />
          )}
          
          {/* Quick Assign Popover */}
          {quickAssign.isOpen && quickAssign.tableInfo && (
            <QuickAssignPopover
              isOpen={quickAssign.isOpen}
              position={quickAssign.position}
              tableInfo={quickAssign.tableInfo}
              unassignedGuests={unassignedGuests}
              assignedGuests={assignedGuests}
              onAssign={handleQuickAssign}
              onRemove={onRemoveGuestFromSeat}
              onSwap={onSwapGuests}
              onClose={closeQuickAssign}
            />
          )}
          
          {/* Empty state */}
          {placedTables.length === 0 && venueElements.length === 0 && customPieces.length === 0 && !backgroundUrl && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className={`text-center ${adminDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                <p className="text-lg mb-2">Drag tables here to arrange them</p>
                <p className="text-sm">Or select from the library on the left</p>
              </div>
            </div>
          )}
          
          {/* Empty state with background */}
          {placedTables.length === 0 && venueElements.length === 0 && customPieces.length === 0 && backgroundUrl && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className={`text-center px-4 py-3 rounded-lg ${adminDarkMode ? 'bg-slate-900/80' : 'bg-white/80'}`}>
                <p className={`text-sm ${adminDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Drag tables to match your venue layout</p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Canvas Info */}
      <div className={`flex items-center justify-between text-xs ${adminDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
        <span>
          {placedTables.length} table(s), {venueElements.length} element(s)
          {customPieces.length > 0 && `, ${customPieces.length} piece(s)`} placed
        </span>
        <span>Canvas: {CANVAS_WIDTH} x {CANVAS_HEIGHT}px | Grid: {GRID_SIZE}px</span>
      </div>
    </div>
  );
}
